import { useCallback, useEffect, useState } from 'react';

import { styled } from '@mui/joy';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';

import { useActsAndDocumentsFormContext } from '@components/ActsAndDocumentsForm';

export const StyledPaddingContainer = styled(Box)`
  padding-right: ${({ theme }) => theme.spacing(2)};
  padding-left: ${({ theme }) => theme.spacing(2)};
`;

export const sx = {
  mt: 'auto',
  paddingTop: theme => theme.spacing(1),
  paddingBottom: theme => theme.spacing(1),
  borderTop: theme => `1px solid ${theme.palette.neutral[300]}`,
  display: 'flex',
  gap: theme => theme.spacing(2),
  justifyContent: 'flex-end',
};

type ActFormFooterProps = {
  disableSubmitIfNotValid?: boolean;
};

export const FormFooter = ({
  showPrevStepButton = true,
  handleCancel,
  prevStepTitle = 'Назад',
  prevStepHandler,
  isPrevStepDisabled = false,
  nextStepTitle = 'Далі',
  nextStepHandler,
  isNextStepDisabled = false,
}: any) => {
  return (
    <StyledPaddingContainer sx={sx}>
      <Button
        sx={{
          mr: 'auto',
          color: theme =>
            theme.palette.isDarkMode
              ? theme.palette.neutral[700]
              : theme.palette.primary[500],
        }}
        variant='plain'
        onClick={handleCancel}
      >
        Скасувати
      </Button>
      {showPrevStepButton && (
        <Button
          variant='plain'
          onClick={prevStepHandler}
          disabled={isPrevStepDisabled}
          sx={{
            color: theme =>
              theme.palette.isDarkMode
                ? theme.palette.neutral[700]
                : theme.palette.primary[500],
          }}
        >
          {prevStepTitle}
        </Button>
      )}
      <Button disabled={isNextStepDisabled} onClick={nextStepHandler}>
        {nextStepTitle}
      </Button>
    </StyledPaddingContainer>
  );
};

export const ActFormFooter = ({
  disableSubmitIfNotValid,
}: ActFormFooterProps) => {
  const [valid, setValid] = useState(false);
  const {
    reset,
    isLastStep,
    moveToNextStep,
    isFirstStep,
    moveToPrevStep,
    validateStep,
    currentStep,
    submit,
    isSubmitting,
    isValidating,
  } = useActsAndDocumentsFormContext();

  const nextStepTitle = isLastStep ? 'Готово' : 'Далі';
  const isDisabled = isSubmitting || isValidating;

  const nextStepAction = isLastStep ? submit : moveToNextStep;

  const nextStepHandler = useCallback(async () => {
    const isValid = await validateStep(currentStep);
    if (isValid) {
      nextStepAction();
    }
  }, [currentStep, nextStepAction, validateStep]);

  useEffect(() => {
    const validate = async () => {
      if (disableSubmitIfNotValid && isLastStep) {
        setValid(await validateStep(currentStep));
      }
    };
    validate();
  }, [disableSubmitIfNotValid, currentStep, validateStep, isLastStep]);

  return (
    <FormFooter
      handleCancel={reset}
      showPrevStepButton={!isFirstStep}
      isPrevStepDisabled={isDisabled}
      prevStepHandler={moveToPrevStep}
      isNextStepDisabled={
        isDisabled || (disableSubmitIfNotValid && isLastStep && !valid)
      }
      nextStepHandler={nextStepHandler}
      nextStepTitle={nextStepTitle}
    />
  );
};
